import React, { useState } from 'react';
import PlanOverview from './PlanOverview';
import ScheduleSurveyModal from './ScheduleSurveyModal';
import { useRunPlan } from '../context/RunPlanContext';

const TrainingPlanSelection = () => {
  const {
    darkMode,
    t: theme,
    trainingGoal,
    setTrainingGoal,
    selectedRaceDistance,
    setSelectedRaceDistance,
    plans,
    selectedPlan,
    setSelectedPlan,
    populate,
    setActiveView,
    createCustomPlan,
    weeks,
    runs,
    getStats,
    setSelectedDate,
    setShowAddRun
  } = useRunPlan();
  const [switchPlanStartDate, setSwitchPlanStartDate] = useState(null);
  const [showScheduleSurvey, setShowScheduleSurvey] = useState(false);
  const [planForSurvey, setPlanForSurvey] = useState(null);

  // Survey handlers
  const handleSurveyComplete = (preferences) => {
    const plan = planForSurvey;
    // Store preferences (you could save to localStorage or context here)
    console.log('User preferences:', preferences);

    // Proceed with plan selection with user preferences
    setSelectedPlan(plan);
    if (switchPlanStartDate) {
      populate(plan, switchPlanStartDate, true, preferences);
      setSwitchPlanStartDate(null);
    } else {
      populate(plan, null, false, preferences);
    }
    setActiveView('calendar');

    // Close survey
    setShowScheduleSurvey(false);
    setPlanForSurvey(null);
  };

  const handleSurveyCancel = () => {
    setShowScheduleSurvey(false);
    setPlanForSurvey(null);
  };

  // If a plan is already selected, show the overview
  if (selectedPlan) {
    const handleSwitchPlan = (startDate) => {
      setSwitchPlanStartDate(startDate);
      setSelectedPlan(null);
      setTrainingGoal(null);
      setSelectedRaceDistance(null);
    };

    return (
      <PlanOverview
        selectedPlan={selectedPlan}
        weeks={weeks}
        runs={runs}
        darkMode={darkMode}
        theme={theme}
        onSwitchPlan={handleSwitchPlan}
        getStats={getStats}
        setSelectedDate={setSelectedDate}
        setShowAddRun={setShowAddRun}
      />
    );
  }

  // Otherwise, show the plan selection UI
  if (!trainingGoal) {
    return (
      <div className="p-4 pb-20">
        <h2 className="text-2xl font-semibold mb-3">What's Your Goal?</h2>
        <p className={darkMode ? 'text-slate-400 mb-6' : 'text-slate-600 mb-6'}>Choose what you'd like to train for</p>

        <div className="space-y-4">
          <button
            onClick={() => setTrainingGoal('start')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🎯</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Start Running</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>New to running? Begin your journey with beginner-friendly programs</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setTrainingGoal('race')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🏁</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Train for a Race</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>Prepare for your next 5K, 10K, half marathon, or marathon</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setTrainingGoal('maintain')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>💪</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Maintain Base</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>Keep your fitness with consistent, sustainable running</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              if (switchPlanStartDate) {
                createCustomPlan(switchPlanStartDate);
                setSwitchPlanStartDate(null);
              } else {
                createCustomPlan();
              }
            }}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>✨</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Create Custom Plan</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>Start with 6 blank weeks and build your own personalized training schedule</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (trainingGoal === 'race' && !selectedRaceDistance) {
    return (
      <div className="p-4 pb-20">
        <button
          onClick={() => {
            setTrainingGoal(null);
            setSelectedRaceDistance(null);
          }}
          className={darkMode ? 'mb-4 text-sm text-slate-400 hover:text-white' : 'mb-4 text-sm text-slate-600 hover:text-slate-900'}
        >
          ← Back to Goals
        </button>
        <h2 className="text-2xl font-semibold mb-3">Choose Your Race Distance</h2>
        <p className={darkMode ? 'text-slate-400 mb-6' : 'text-slate-600 mb-6'}>Select the distance you want to train for</p>

        <div className="space-y-4">
          <button
            onClick={() => setSelectedRaceDistance('5K')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🏃</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">5K</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>3.1 miles - Perfect for beginners and speed work</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setSelectedRaceDistance('10K')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🏃‍♂️</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">10K</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>6.2 miles - Great stepping stone to longer distances</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setSelectedRaceDistance('Half Marathon')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🏃‍♀️</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Half Marathon</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>13.1 miles - Challenging endurance race</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setSelectedRaceDistance('Marathon')}
            className={darkMode ? 'w-full text-left p-6 rounded-2xl border-2 bg-slate-800 border-slate-700 hover:border-sky-500 transition-all' : 'w-full text-left p-6 rounded-2xl border-2 bg-white border-slate-200 hover:border-sky-500 transition-all'}
          >
            <div className="flex items-start gap-4">
              <div className={'text-3xl p-3 rounded-xl ' + theme.light}>🏁</div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Marathon</h3>
                <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>26.2 miles - Ultimate endurance challenge</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (trainingGoal === 'race' && selectedRaceDistance) {
    return (
      <>
        <div className="p-4 pb-20">
          <button
            onClick={() => setSelectedRaceDistance(null)}
            className={darkMode ? 'mb-4 text-sm text-slate-400 hover:text-white' : 'mb-4 text-sm text-slate-600 hover:text-slate-900'}
          >
            ← Back to Race Distances
          </button>
          <h2 className="text-2xl font-semibold mb-3">{selectedRaceDistance} Training Plans</h2>
          <p className={darkMode ? 'text-slate-400 mb-6' : 'text-slate-600 mb-6'}>Choose a plan that matches your experience level</p>

          <div className="space-y-4">
            {plans.filter(p => p.goal === 'race' && p.raceType === selectedRaceDistance).map(plan => (
              <div key={plan.id} className={darkMode ? 'border rounded-2xl p-5 bg-slate-800 border-slate-700' : 'border rounded-2xl p-5 bg-white border-slate-200'}>
                <div className="flex justify-between mb-3">
                  <h3 className="text-xl font-semibold">{plan.name}</h3>
                  <span className={'px-3 py-1 rounded-full text-xs ' + theme.light + ' ' + theme.lightText}>{plan.diff}</span>
                </div>
                <p className={darkMode ? 'mb-4 text-sm text-slate-400' : 'mb-4 text-sm text-slate-600'}>{plan.desc}</p>
                <button onClick={() => {
                  setPlanForSurvey(plan);
                  setShowScheduleSurvey(true);
                }} className={'w-full text-white py-3 rounded-xl font-medium ' + theme.primary + ' ' + theme.hover}>Select Plan</button>
              </div>
            ))}
          </div>
        </div>

        <ScheduleSurveyModal
          isOpen={showScheduleSurvey}
          plan={planForSurvey}
          darkMode={darkMode}
          theme={theme}
          onComplete={handleSurveyComplete}
          onCancel={handleSurveyCancel}
        />
      </>
    );
  }

  return (
    <>
      <div className="p-4 pb-20">
        <button
          onClick={() => setTrainingGoal(null)}
          className={darkMode ? 'mb-4 text-sm text-slate-400 hover:text-white' : 'mb-4 text-sm text-slate-600 hover:text-slate-900'}
        >
          ← Back to Goals
        </button>
        <h2 className="text-2xl font-semibold mb-6">
          {trainingGoal === 'start' ? 'Start Running' : 'Maintain Base'}
        </h2>
        <div className="space-y-4">
          {plans.filter(p => p.goal === trainingGoal).map(plan => (
            <div key={plan.id} className={darkMode ? 'border rounded-2xl p-5 bg-slate-800 border-slate-700' : 'border rounded-2xl p-5 bg-white border-slate-200'}>
              <div className="flex justify-between mb-3">
                <h3 className="text-xl font-semibold">{plan.name}</h3>
                <span className={'px-3 py-1 rounded-full text-xs ' + theme.light + ' ' + theme.lightText}>{plan.diff}</span>
              </div>
              <p className={darkMode ? 'mb-4 text-sm text-slate-400' : 'mb-4 text-sm text-slate-600'}>{plan.desc}</p>
              <button onClick={() => {
                setPlanForSurvey(plan);
                setShowScheduleSurvey(true);
              }} className={'w-full text-white py-3 rounded-xl font-medium ' + theme.primary + ' ' + theme.hover}>Select</button>
            </div>
          ))}
        </div>
      </div>

      <ScheduleSurveyModal
        isOpen={showScheduleSurvey}
        plan={planForSurvey}
        darkMode={darkMode}
        theme={theme}
        onComplete={handleSurveyComplete}
        onCancel={handleSurveyCancel}
      />
    </>
  );
};

export default TrainingPlanSelection;
